/**
 * Scroll story: wheel/touch/keyboard input pans a horizontal track instead of the
 * page scrolling vertically, with eased motion and per-layer parallax depth. Works at
 * any viewport width — touch drag (combining vertical and horizontal movement) drives
 * the same engine wheel does on desktop. Disabled outright under prefers-reduced-motion,
 * where the section renders as a normal stacked, vertically-scrolling block (see CSS).
 */
(function () {
  'use strict';

  if (customElements.get('scroll-story-component')) return;

  const EASE = 0.08;
  const SETTLE_THRESHOLD = 0.5;
  const KEY_STEP_RATIO = 0.8;
  const RELEASE_PAUSE_MS = 500;
  // A physical swipe is capped at roughly the screen's height (~500-800px), while the
  // track can run for several thousand pixels — dragging 1:1 with a finger would take
  // many repeated swipes to get anywhere. Wheel/trackpad input doesn't have that
  // physical ceiling, so this only boosts touch dragging, not scroll_sensitivity itself.
  const TOUCH_DRAG_MULTIPLIER = 3;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  class ScrollStoryComponent extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.scroll-story__viewport');
      this.track = this.querySelector('[data-scroll-track]');
      if (!this.viewport || !this.track) return;

      this.collectLayers();

      this.sensitivity = parseFloat(this.dataset.sensitivity) || 1;
      this.current = 0;
      this.target = 0;
      this.maxScroll = 0;
      this.rafId = null;
      this.enhanced = false;
      this.intersecting = false;
      this.inputAttached = false;
      this.dragging = false;
      this.startReachedAt = null;
      this.endReachedAt = null;

      this.onWheel = this.onWheel.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onIntersect = this.onIntersect.bind(this);
      this.onMutation = this.onMutation.bind(this);
      this.tick = this.tick.bind(this);

      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotionQuery.addEventListener('change', this.onResize);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.intersectionObserver = new IntersectionObserver(this.onIntersect, { threshold: 0 });
      this.intersectionObserver.observe(this);

      // Merchants add/remove/reorder blocks from the theme editor, which can patch the
      // track's contents without the custom element itself disconnecting and
      // reconnecting. Without this, maxScroll and the parallax layer list would stay
      // stale, and the wheel handler would release control back to page scroll before
      // reaching the real end of the (now longer) track.
      this.mutationObserver = new MutationObserver(this.onMutation);
      this.mutationObserver.observe(this.track, { childList: true, subtree: true });

      this.evaluateEnhancement();
    }

    disconnectedCallback() {
      this.detachInputListeners();
      this.stopLoop();
      clearTimeout(this.releaseTimer);
      this.intersectionObserver?.disconnect();
      this.mutationObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      this.reducedMotionQuery?.removeEventListener('change', this.onResize);
    }

    collectLayers() {
      this.layers = Array.from(this.querySelectorAll('[data-parallax-speed]')).map((layer) => ({
        element: layer,
        speed: parseFloat(layer.dataset.parallaxSpeed) || 0,
        insideTrack: this.track.contains(layer),
      }));
    }

    onMutation() {
      this.collectLayers();
      this.syncVideoMotion();
      if (this.enhanced) this.measure();
    }

    evaluateEnhancement() {
      const shouldEnhance = !this.reducedMotionQuery.matches;

      this.syncVideoMotion();

      if (shouldEnhance === this.enhanced) {
        if (shouldEnhance) this.measure();
        return;
      }

      this.enhanced = shouldEnhance;
      this.classList.toggle('is-enhanced', shouldEnhance);

      if (shouldEnhance) {
        this.measure();
      } else {
        this.resetPosition();
      }

      this.syncInputListeners();
    }

    syncVideoMotion() {
      const videos = this.querySelectorAll('.scroll-story__panel--video video');
      videos.forEach((video) => {
        if (this.reducedMotionQuery.matches) {
          video.pause();
        } else if (video.hasAttribute('autoplay')) {
          video.play().catch(() => {});
        }
      });
    }

    onResize() {
      this.evaluateEnhancement();
    }

    onIntersect(entries) {
      this.intersecting = entries[entries.length - 1].isIntersecting;
      this.syncInputListeners();
      if (!this.intersecting) this.stopLoop();
    }

    syncInputListeners() {
      const shouldAttach = this.enhanced && this.intersecting;
      if (shouldAttach && !this.inputAttached) {
        this.attachInputListeners();
      } else if (!shouldAttach && this.inputAttached) {
        this.detachInputListeners();
      }
    }

    attachInputListeners() {
      this.inputAttached = true;
      this.addEventListener('wheel', this.onWheel, { passive: false });
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('pointerdown', this.onPointerDown);
    }

    detachInputListeners() {
      this.inputAttached = false;
      this.removeEventListener('wheel', this.onWheel);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.endDrag();
    }

    measure() {
      if (!this.enhanced) return;
      const viewportWidth = this.viewport.getBoundingClientRect().width;
      const trackWidth = this.track.scrollWidth;
      let maxScroll = Math.max(0, trackWidth - viewportWidth);

      // A panel with parallax_speed < 1 renders at layoutRight - current * speed (see
      // render()), lagging behind the raw scroll for its depth effect. If maxScroll were
      // left at the raw track width, panels with a slow speed positioned late in the
      // track would never fully scroll into view before the wheel handler releases
      // control back to the page — the more current is required to catch up, the more
      // room the track needs. Take whichever panel needs the most.
      this.layers.forEach(({ element, speed, insideTrack }) => {
        if (!insideTrack || speed <= 0) return;
        const rightEdge = element.offsetLeft + element.offsetWidth;
        const required = (rightEdge - viewportWidth) / speed;
        if (required > maxScroll) maxScroll = required;
      });

      this.maxScroll = maxScroll;
      this.current = clamp(this.current, 0, this.maxScroll);
      this.target = clamp(this.target, 0, this.maxScroll);
      this.render();
    }

    resetPosition() {
      this.current = 0;
      this.target = 0;
      this.startReachedAt = null;
      this.endReachedAt = null;
      clearTimeout(this.releaseTimer);
      this.classList.remove('scroll-story--released');
      this.stopLoop();
      this.track.style.transform = '';
      this.layers.forEach(({ element }) => {
        element.style.transform = '';
      });
      this.style.removeProperty('--story-progress');
    }

    // Only the *end* is eligible for a touch release, never the start: current is 0
    // by default before anyone has interacted at all, which isn't the same thing as
    // "arrived back at the start after finishing" — releasing there would mean the very
    // first swipe on page load falls straight through to page scroll, and the story
    // never engages on mobile at all.
    isAtReleasableBoundary() {
      return this.maxScroll > 0 && this.current >= this.maxScroll - SETTLE_THRESHOLD;
    }

    // Wheel input releases per-event based on its own delta (see onWheel), but a touch
    // gesture is captured as a whole via touch-action, decided at the moment it starts.
    // So instead we toggle a class once the pause has elapsed while sitting at the end
    // — CSS uses it to switch touch-action back to a native scroll for the *next*
    // gesture, letting the user swipe on past the section on mobile.
    scheduleBoundaryRelease() {
      clearTimeout(this.releaseTimer);
      this.classList.remove('scroll-story--released');
      this.releaseTimer = setTimeout(() => {
        if (this.isAtReleasableBoundary()) this.classList.add('scroll-story--released');
      }, RELEASE_PAUSE_MS);
    }

    onWheel(event) {
      if (this.maxScroll <= 0) return;

      const delta = (event.deltaY + event.deltaX) * this.sensitivity;
      if (delta === 0) return;

      const atStart = this.target <= 0;
      const atEnd = this.target >= this.maxScroll;
      if ((atStart && delta < 0) || (atEnd && delta > 0)) {
        // Already at an edge and still scrolling outward. Rather than releasing to
        // page scroll the instant the boundary is reached, absorb wheel input for a
        // short grace period after settling there — an abrupt handoff into the
        // footer feels jarring right as the story finishes.
        const reachedAt = atEnd ? this.endReachedAt : this.startReachedAt;
        const pastPause = reachedAt !== null && performance.now() - reachedAt >= RELEASE_PAUSE_MS;
        if (!pastPause) event.preventDefault();
        return;
      }

      event.preventDefault();
      this.target = clamp(this.target + delta, 0, this.maxScroll);
      this.startLoop();
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (this.maxScroll <= 0) return;

      event.preventDefault();
      const step = this.viewport.getBoundingClientRect().width * KEY_STEP_RATIO;
      const delta = event.key === 'ArrowRight' ? step : -step;
      this.target = clamp(this.target + delta, 0, this.maxScroll);
      this.startLoop();
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' || this.maxScroll <= 0) return;

      this.dragging = true;
      this.dragStartX = event.clientX;
      this.dragStartY = event.clientY;
      this.dragStartTarget = this.target;
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      // Combine both axes so the natural "swipe up to keep scrolling" gesture works
      // the same as a deliberate left/right swipe — mirrors deltaY + deltaX on wheel.
      const delta =
        (this.dragStartY - event.clientY + (this.dragStartX - event.clientX)) *
        this.sensitivity *
        TOUCH_DRAG_MULTIPLIER;
      this.target = clamp(this.dragStartTarget + delta, 0, this.maxScroll);
      this.startLoop();
    }

    onPointerUp() {
      this.endDrag();
    }

    endDrag() {
      this.dragging = false;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    startLoop() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(this.tick);
    }

    stopLoop() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    tick() {
      const delta = this.target - this.current;

      if (Math.abs(delta) < SETTLE_THRESHOLD) {
        this.current = this.target;
        this.render();
        this.rafId = null;
        return;
      }

      this.current += delta * EASE;
      this.render();
      this.rafId = requestAnimationFrame(this.tick);
    }

    render() {
      this.track.style.transform = `translate3d(${-this.current}px, 0, 0)`;

      this.layers.forEach(({ element, speed, insideTrack }) => {
        // Layers inside the track already inherit its full -current translation, so
        // their own offset only needs to make up the difference for the target speed:
        // net displacement = -current + current * (1 - speed) = -current * speed.
        // Layers outside the track (the background wordmark) get the speed applied
        // directly since there's no parent transform to net against.
        const offset = insideTrack ? this.current * (1 - speed) : -this.current * speed;
        element.style.transform = `translate3d(${offset}px, 0, 0)`;
      });

      const progress = this.maxScroll > 0 ? this.current / this.maxScroll : 0;
      this.style.setProperty('--story-progress', progress.toFixed(4));

      // Track how long we've been sitting at each boundary, so onWheel can hold off
      // releasing to page scroll for a beat rather than handing off the instant the
      // track arrives. Cleared as soon as we're no longer there.
      const wasAtEnd = this.endReachedAt !== null;
      this.startReachedAt = this.current <= SETTLE_THRESHOLD ? this.startReachedAt ?? performance.now() : null;
      this.endReachedAt =
        this.maxScroll > 0 && this.current >= this.maxScroll - SETTLE_THRESHOLD
          ? this.endReachedAt ?? performance.now()
          : null;
      const atEnd = this.endReachedAt !== null;

      if (atEnd && !wasAtEnd) {
        this.scheduleBoundaryRelease();
      } else if (!atEnd && wasAtEnd) {
        clearTimeout(this.releaseTimer);
        this.classList.remove('scroll-story--released');
      }
    }
  }

  customElements.define('scroll-story-component', ScrollStoryComponent);
})();
